import { getUrlForProduct, isSodimacStandalone, isWhitelabel } from '@digital-retail/store-manager';
import getConfig from 'next/config';
import { useEffect, useRef, useState } from 'react';
import constants from '../../config/constants';
import { CookieStorage } from '../../utils/CookieStorage';
import _ from '../../utils/LodashImports';
import { getContextIdFromCookie, getWidgetsApiParams, groupByBackendCategoryId } from '../../utils/RecoHelper';
import { usePriceMapper } from '../../utils/hooks/usePriceMapper';
import { usePriceMapperV2 } from '../../utils/hooks/usePriceMapperV2';
import { carouselActions, recoAnalytics } from '../../utils/recoAnalytics';
import getLabels from './ShopTheProject.labels';
import { excludeMainProduct, mapClickMetrics } from './ShopTheProject.utils';

const { publicRuntimeConfig } = getConfig();

const localStorageKeys = {
  shopTheProject: 'shop-the-project-already-seen-by-user',
  ambientImage: 'ambient-image-already-seen-by-user',
};

const configToggles = {
  shopTheProject: 'isShopTheProjectCoachmarkEnabled',
  ambientImage: 'isShopTheProjectAmbientImageCoachmarkEnabled',
};

const slotsComplementaryProducts = 'PDP-PRJ-1';
const slotsSimilarProducts = 'PDP-PRJ-2';
const minComplementaryProducts = 3;
const maxComplementaryProducts = 5;
const maxSimilarProducts = 5;
const maxComplementaryProductsForAmbientImage = 10;

const COORDINATE_COUNT_LIMIT_SINGLE = 1;
const ALL_COORDINATES = 'ALL';

const AMBIENT_IMAGE_ANALYTICS_PREFIX = 'Lifestyle_';

const storeCodes = {
  [constants.STORES.sodimac]: 'SO',
  [constants.STORES.tottus]: 'TO',
  [constants.STORES.linio]: 'LI',
  [constants.STORES.tienda]: 'TI',
  [constants.STORES.homecenter]: 'SO',
  [constants.STORES.so_com]: 'SO',
  [constants.STORES.so_retail]: 'SO',
  [constants.STORES.to_com]: 'TO',
};

export const useShopTheProject = ({
  activeImageIndex,
  mainProductImage,
  appCtx,
  product,
  cart,
  userInteraction = 'shopTheProject',
  setIsCoachmarkVisible,
  hasAmbientImage = true,
  ambientImageCategoryEnabled = true,
}) => {
  const projectNamePrimary = useRef(undefined);
  const projectNameSecondary = useRef(undefined);
  const storeCode = storeCodes[appCtx.store];
  const storeAndCountryCode = `${storeCode}${appCtx.regionCode.toUpperCase()}`;
  const [showCoachmark, setShowCoachmark] = useState(undefined);
  const [initialPrimaryProducts, setInitialPrimaryProducts] = useState([]);
  const { mapPrices } = usePriceMapper();
  const { mapPricesV2 } = usePriceMapperV2();
  const labels = getLabels(appCtx);
  const [useAmbientImage, setUseAmbientImage] = useState(false);
  const [mappedCoordinates, setMappedCoordinates] = useState([]);
  const ambientImageMediaUrl = mainProductImage.replace('/public', '');
  const [widgetUUID, setWidgetUUID] = useState('');
  const [AnalyticsPrefix, setAnalyticsPrefix] = useState('');

  const coordinateLimitFirstCall = hasAmbientImage && ambientImageCategoryEnabled;

  const mapRawProducts = (response, type, variantId) => {
    const rawProducts = _.get(response, '[0].data', []);
    return rawProducts.map((fetchedProduct) => ({
      ...fetchedProduct,
      variantId: fetchedProduct.offeringId,
      price: mapPrices(fetchedProduct),
      type,
      driverVariantId: variantId,
    }));
  };

  const mapRawProductsFromCoordinates = (coordinates, type, variantId, limit = 1) => {
    return coordinates
      .filter((coord) => !coord.isDefault && coord.isSelected)
      .flatMap((coord) => {
        const selectedProducts = _.get(coord, `products`, []);
        return selectedProducts.slice(0, limit).map((rawProduct) => {
          return {
            ...rawProduct,
            variantId: rawProduct.offeringId,
            price: mapPrices(rawProduct),
            type,
            driverVariantId: variantId,
            coordinate: coord.coordinate,
            coordinateId: coord.id,
            similarProduct: true,
          };
        });
      });
  };

  const fetchProducts = async ({
    productId,
    variantId,
    slots,
    categoryId,
    webCategoryId,
    brandName,
    sellerId,
    type,
    selectedCoordinate,
    coordinateCountLimit,
  }) => {
    const contextId = getContextIdFromCookie(appCtx);
    const site = appCtx.store;
    const isSodimacStandaloneChannel = _.get(
      appCtx,
      `siteConfig.configurations.shopTheProjectConfig.channel`,
      'standalone_web'
    );
    const channel = isSodimacStandalone(site) ? isSodimacStandaloneChannel : 'web';
    const widgetsUUID = widgetUUID;
    const userId = CookieStorage.getCookie('USER_ID') || '';
    const response = await product.loadRecommendedProducts({
      ...getWidgetsApiParams({
        appCtx,
        product: {
          id: productId,
          currentVariant: variantId,
          attributes: {
            GSCCategoryId: categoryId,
          },
          defaultParentCategoryId: webCategoryId,
          brandName,
          sellerInfo: { sellerId },
        },
        slots,
        widgetsUUID,
        userId,
        contextId,
        channel,
        selectedCoordinate,
        coordinateCountLimit,
      }),
      selectedCoordinate,
      coordinateCountLimit,
    });
    const name = _.get(response, '[0].name');
    const id = _.get(response, '[0].id', '');
    const coordinates = _.get(response, '[0].coordinates');
    const products = mapRawProducts(response, type, variantId);
    const imageUrl = _.get(response, '[0].imageUrl', null);
    return { id, name, products, coordinates, imageUrl };
  };

  const { store, regionCode } = appCtx;

  const mainProduct = {
    productId: product.id,
    variantId: product.currentVariant,
    displayName: product.name,
    brand: product.brandName,
    price: mapPricesV2(_.get(product, 'variants[0]', {})),
    url: getUrlForProduct({
      productId: product.id,
      skuId: product.currentVariant,
      productName: product.name,
      productSlug: product.slug,
      regionCode,
      environment: publicRuntimeConfig.ENVIRONMENT ?? 'production',
      store,
      isWhiteLabelExperience: isWhitelabel({ store }),
    }).toString(),
    sellerName: product.sellerInfo.sellerName,
    mediaUrls: [mainProductImage],
  };

  const mapCoordinates = (rawCoordinates) => {
    return rawCoordinates
      .filter((coord) => !coord.isDefault)
      .map((coord) => ({
        coordinateId: coord.id,
        coordinate: coord.coordinate,
      }));
  };

  const initializeCoachmarkVisibility = () => {
    const isCoachmarkEnabled = _.get(appCtx, `siteConfig.toggles.${configToggles[userInteraction]}`, false);
    const isUserFirstRender = window?.localStorage?.getItem(localStorageKeys[userInteraction]) === null;
    const shouldShowCoachmark = isCoachmarkEnabled && isUserFirstRender;
    setShowCoachmark(shouldShowCoachmark);
    setIsCoachmarkVisible?.(shouldShowCoachmark);
    if (shouldShowCoachmark) window?.localStorage?.setItem(localStorageKeys[userInteraction], true);
  };

  useEffect(() => {
    (async () => {
      initializeCoachmarkVisibility();
      const widgetUserExperienceId = userInteraction === 'ambientImage' ? 'DOTS_STP_' : 'STP_Widget_';

      const { id, name, products, coordinates } = await fetchProducts({
        productId: product.id,
        variantId: product.currentVariant,
        slots: slotsComplementaryProducts,
        categoryId: _.get(product, 'attributes.GSCCategoryId', ''),
        webCategoryId: product.defaultParentCategoryId,
        brandName: product.brandName,
        sellerId: _.get(product, 'variants[0].offerings[0].sellerId', ''),
        type: 'complement',
        ...(coordinateLimitFirstCall ? { coordinateCountLimit: COORDINATE_COUNT_LIMIT_SINGLE } : {}),
      });

      if (coordinates?.length > 0) {
        setInitialPrimaryProducts([]);
        setUseAmbientImage(true);
        setMappedCoordinates(mapCoordinates(coordinates));
        setWidgetUUID(id);
        setAnalyticsPrefix(AMBIENT_IMAGE_ANALYTICS_PREFIX);
        recoAnalytics.impression({
          carouselName: `${AMBIENT_IMAGE_ANALYTICS_PREFIX}${widgetUserExperienceId}${name}`,
          skusViewed: [product.currentVariant],
        });
      } else {
        const primaryProducts = groupByBackendCategoryId({
          products,
          filterOptions: {
            maxGroups: maxComplementaryProducts,
            podsByGroup: 1,
            minPods: minComplementaryProducts,
          },
        });
        setInitialPrimaryProducts(primaryProducts);
        if (primaryProducts.length) {
          recoAnalytics.impression({
            carouselName: `STP_Widget_${name}`,
            skusViewed: [product.currentVariant],
          });
        }
      }
      projectNamePrimary.current = name;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddToCart = async (productsToAddToCart) => {
    const productsData = productsToAddToCart.map((productToAddToCart) => ({
      skuId: productToAddToCart.variantId,
      productId: productToAddToCart.productId,
      quantity: productToAddToCart.quantity,
      offeringId: productToAddToCart.variantId,
    }));
    return cart.addProduct({
      appCtx,
      products: productsData,
      product,
    });
  };

  const handleImpression = ({ driverProduct, visibleVariant, carouselName }) => {
    const { variantId: driverVariantId } = driverProduct;
    const { variantId: visibleVariantId } = visibleVariant;
    if (driverVariantId === visibleVariantId) {
      return;
    }
    recoAnalytics.impression({
      carouselName: `${AnalyticsPrefix}STP_${carouselName}`,
      skusViewed: [visibleVariantId],
      driverVariantId,
    });
  };

  const getSecondaryProducts = async (primaryProduct) => {
    const { name, products } = await fetchProducts({
      productId: primaryProduct.productId,
      variantId: primaryProduct.variantId,
      slots: slotsSimilarProducts,
      categoryId: '',
      webCategoryId: '',
      brandName: primaryProduct.brand,
      sellerId: primaryProduct.sellerName,
      type: 'similar',
    });
    projectNameSecondary.current = name;
    return products.slice(0, maxSimilarProducts).map((secondaryProduct) => ({
      ...secondaryProduct,
      variantId: secondaryProduct.offeringId,
    }));
  };

  const getSecondaryProductsWithCoordinate = async (primaryProduct) => {
    const { name, coordinates } = await fetchProducts({
      productId: mainProduct.productId,
      variantId: mainProduct.variantId,
      slots: slotsComplementaryProducts,
      categoryId: '',
      webCategoryId: '',
      brandName: mainProduct.brand,
      sellerId: mainProduct.sellerName,
      type: 'complement',
      selectedCoordinate: primaryProduct.coordinateId,
      coordinateCountLimit: maxSimilarProducts + 1,
    });
    projectNameSecondary.current = name;
    const products = coordinates
      ? mapRawProductsFromCoordinates(coordinates, 'similar', mainProduct.variantId, maxSimilarProducts + 1)
      : [];
    return products.slice(1, maxSimilarProducts + 1).map((secondaryProduct) => ({
      ...secondaryProduct,
      variantId: secondaryProduct.offeringId,
    }));
  };

  const getPrimaryProducts = async () => {
    const { name, coordinates } = await fetchProducts({
      productId: mainProduct.productId,
      variantId: mainProduct.variantId,
      slots: slotsComplementaryProducts,
      categoryId: _.get(product, 'attributes.GSCCategoryId', ''),
      webCategoryId: product.defaultParentCategoryId,
      brandName: mainProduct.brandName,
      sellerId: _.get(product, 'variants[0].offerings[0].sellerId', ''),
      selectedCoordinate: ALL_COORDINATES,
      coordinateCountLimit: COORDINATE_COUNT_LIMIT_SINGLE,
    });
    projectNamePrimary.current = name;
    const products = coordinates
      ? mapRawProductsFromCoordinates(coordinates, 'complement', product.currentVariant, 1)
      : [];
    return products.slice(0, maxComplementaryProductsForAmbientImage);
  };

  const getPrimaryProduct = async (coordinateId) => {
    const { name, coordinates } = await fetchProducts({
      productId: mainProduct.productId,
      variantId: mainProduct.variantId,
      slots: slotsComplementaryProducts,
      categoryId: _.get(product, 'attributes.GSCCategoryId', ''),
      webCategoryId: product.defaultParentCategoryId,
      brandName: mainProduct.brandName,
      sellerId: _.get(product, 'variants[0].offerings[0].sellerId', ''),
      type: 'complement',
      selectedCoordinate: coordinateId,
      coordinateCountLimit: COORDINATE_COUNT_LIMIT_SINGLE,
    });
    projectNamePrimary.current = name;
    const products = mapRawProductsFromCoordinates(coordinates, 'complement', mainProduct.variantId, 1);
    return products && products.length > 0 ? products[0] : null;
  };

  const onClickOnCallToAction = (primaryProducts) => {
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      carouselName: `${AnalyticsPrefix}${projectNamePrimary.current}`,
      pageName: mainProduct.variantId,
      podPositionInCarousel: activeImageIndex,
      lengthPodsInCarousel: excludeMainProduct(primaryProducts).length,
      slotId: slotsComplementaryProducts,
    });
  };

  const onPrimaryProductImpression = (driverProduct, complementaryProduct) => {
    handleImpression({
      driverProduct,
      visibleVariant: complementaryProduct,
      carouselName: projectNamePrimary.current,
    });
  };

  const onSecondaryProductImpression = (complementaryProduct, similaryProduct) => {
    handleImpression({
      driverProduct: complementaryProduct,
      visibleVariant: similaryProduct,
      carouselName: projectNameSecondary.current,
    });
  };

  const onAddToCart = (primaryProductsOnCart, allPrimaryProducts) => {
    const { selectedVariantsIds, podPositions, podsInCarousel } = mapClickMetrics(
      primaryProductsOnCart,
      allPrimaryProducts
    );
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      action: carouselActions.addToCart,
      carouselName: `${AnalyticsPrefix}${projectNamePrimary.current}`,
      pageName: mainProduct.variantId,
      skuId: selectedVariantsIds,
      podPositionInCarousel: podPositions,
      lengthPodsInCarousel: podsInCarousel.length,
      slotId: slotsComplementaryProducts,
    });
  };

  const onClickOnProductName = (clickedProduct, allProducts) => {
    const configByProject = {
      complement: {
        carouselName: `${AnalyticsPrefix}${projectNamePrimary.current}`,
        slotId: slotsComplementaryProducts,
      },
      similar: {
        carouselName: `${AnalyticsPrefix}${projectNameSecondary.current}`,
        slotId: useAmbientImage ? slotsComplementaryProducts : slotsSimilarProducts,
      },
    };
    const projectConfig = configByProject[clickedProduct.type] || configByProject.complement;
    const { podPositions: productPosition, podsInCarousel } = mapClickMetrics([clickedProduct], allProducts);
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      action: carouselActions.clickToRate,
      carouselName: projectConfig.carouselName,
      pageName: clickedProduct.driverVariantId,
      skuId: clickedProduct.variantId,
      podPositionInCarousel: productPosition,
      lengthPodsInCarousel: podsInCarousel.length,
      slotId: projectConfig.slotId,
    });
  };

  const onClickOnExchange = (primaryProduct, secondaryProduct, _allPrimaryProduct, allSecondaryProduct) => {
    const productPosition = allSecondaryProduct.findIndex(
      (productInArray) => productInArray.variantId === secondaryProduct.variantId
    );
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      carouselName: `${AnalyticsPrefix}${projectNameSecondary.current}`,
      pageName: primaryProduct.variantId,
      skuId: secondaryProduct.variantId,
      podPositionInCarousel: productPosition + 1,
      lengthPodsInCarousel: allSecondaryProduct.length,
      slotId: useAmbientImage ? slotsComplementaryProducts : slotsSimilarProducts,
    });
  };

  const onClickOnDot = (primaryProduct, allProducts) => {
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      carouselName: `${AnalyticsPrefix}${projectNamePrimary.current}`,
      pageName: mainProduct.variantId,
      skuId: primaryProduct.variantId,
      lengthPodsInCarousel: allProducts.length,
      slotId: slotsComplementaryProducts,
      action: 'DOT',
    });
  };

  const onClickOnTooltip = (primaryProduct, allProducts) => {
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      carouselName: `${AnalyticsPrefix}${projectNamePrimary.current}`,
      pageName: mainProduct.variantId,
      skuId: primaryProduct.variantId,
      lengthPodsInCarousel: allProducts.length,
      slotId: slotsComplementaryProducts,
      action: 'Detail_link',
    });
  };

  const analytics = {
    onClickOnCallToAction,
    onPrimaryProductImpression,
    onSecondaryProductImpression,
    onAddToCart,
    onClickOnProductName,
    onClickOnExchange,
    onClickOnDot,
    onClickOnTooltip,
  };

  return {
    initialPrimaryProducts,
    minComplementaryProducts,
    labels,
    showCoachmark,
    maxComplementaryProducts,
    mainProduct,
    handleAddToCart,
    getSecondaryProducts: useAmbientImage ? getSecondaryProductsWithCoordinate : getSecondaryProducts,
    analytics,
    useAmbientImage,
    getPrimaryProducts,
    ambientImageMediaUrl,
    mappedCoordinates,
    getPrimaryProduct,
  };
};
